import {
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  Skeleton,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { convert } from "html-to-text";
import Cookies from "js-cookie";

const questionsType = ["Descriptive", "Numeric", "Yes/No"];

// ── Card header style ──────────────────────────────────────────────────────────
const cardHeaderSx = {
  borderRadius: "4px 4px 0 0",
  padding: "14px 20px",
  backgroundColor: "rgba(63, 149, 234, 0.20)",
  color: "#000",
  fontSize: "14px",
  fontWeight: "600",
  flexShrink: 0,
};

const cardSx = {
  borderRadius: "4px",
  border: "1px solid rgba(115, 119, 145, 0.08)",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  overflowY: "hidden",
  elevation: 0,
  boxShadow: "none",
};

// ── Basic Details Shimmer ──────────────────────────────────────────────────────
const BasicDetailsShimmer = () => (
  <Card sx={cardSx}>
    <Box sx={cardHeaderSx}>Basic Details</Box>
    <CardContent sx={{ padding: "16px 20px", overflowY: "auto", flexGrow: 1 }}>
      {Array.from({ length: 10 }).map((_, i) => (
        <Box
          key={i}
          sx={{ display: "flex", alignItems: "center", gap: 2, mb: "12px" }}
        >
          <Skeleton
            variant="text"
            width={100}
            height={18}
            sx={{ flexShrink: 0 }}
          />
          <Skeleton
            variant="text"
            width={`${50 + Math.random() * 30}%`}
            height={18}
          />
        </Box>
      ))}
    </CardContent>
  </Card>
);

// ── Other Details Shimmer ──────────────────────────────────────────────────────
const OtherDetailsShimmer = () => (
  <Card sx={cardSx}>
    <Box sx={cardHeaderSx}>Other Details</Box>
    <CardContent sx={{ padding: "16px 20px", overflowY: "auto", flexGrow: 1 }}>
      {/* Description */}
      <Skeleton variant="text" width={90} height={20} sx={{ mb: "8px" }} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="80%" height={16} />
      <Skeleton variant="text" width="60%" height={16} sx={{ mb: "12px" }} />

      <Divider sx={{ margin: "12px 0" }} />

      <Skeleton variant="text" width={140} height={20} sx={{ mb: "8px" }} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="85%" height={16} sx={{ mb: "12px" }} />

      <Divider sx={{ margin: "12px 0" }} />

      {/* Skills */}
      <Skeleton variant="text" width={50} height={20} sx={{ mb: "10px" }} />
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            width={60 + i * 10}
            height={26}
            sx={{ borderRadius: "16px" }}
          />
        ))}
      </Box>
    </CardContent>
  </Card>
);

// ── Internal Notes Shimmer ─────────────────────────────────────────────────────
const InternalNotesShimmer = () => (
  <Card sx={cardSx}>
    <Box sx={cardHeaderSx}>Internal Notes</Box>
    <CardContent sx={{ padding: "16px 20px", overflowY: "auto", flexGrow: 1 }}>
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="75%" height={16} />
      <Skeleton variant="text" width="90%" height={16} sx={{ mt: "12px" }} />
      <Skeleton variant="text" width="100%" height={16} />
      <Skeleton variant="text" width="60%" height={16} />
    </CardContent>
  </Card>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const JobInfo = ({ isDrawerOpen, id: propId }) => {
  const { id: paramId } = useParams();
  const id = propId || paramId;

  const [jobData, setJobData] = useState({});
  const [questionsData, setQuestionsData] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [loading, setLoading] = useState(true); // ← shimmer state
  const descriptionRef = useRef(null);

  const toggleShowMore = () => setShowMore((prev) => !prev);

  useEffect(() => {
    if (descriptionRef.current && !showMore) {
      const el = descriptionRef.current;
      setTimeout(() => {
        setIsOverflowing(el.scrollHeight > el.clientHeight);
      }, 0);
    }
  }, [jobData?.otherDetails?.description, showMore]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchJobDetails(), getQuestionsData()]);
      setLoading(false);
    };
    fetchAll();
  }, [id, isDrawerOpen]);

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(`job-info/${id}`, {
        headers: {
          authorization:
            Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
        },
      });
      const raw = response.data.message.jobInfoResult[0];
      console.log("raw: ", raw);

      const myTypesReverse = {
        H: "Hire",
        C: "Contract",
        C2H: "Contract to Hire",
        F: "Freelance",
      };

      const basicDetails = {
        "Job Title": raw?.title || "NA",
        Company: raw?.name || "NA",

        ...(raw?.workmode?.toLowerCase() !== "remote" && {
          Location: `${raw?.city || "NA"}, ${raw?.state || "NA"}`,
        }),

        "Job Type": myTypesReverse[raw?.type] || raw?.type || "NA",
        "Work Mode": raw?.workmode || "NA",
        Experience:
          raw?.min_experience != null && raw?.max_experience != null
            ? `${raw.min_experience} - ${raw.max_experience} Years`
            : "NA",
        Openings: raw?.openings || "NA",
        Salary: raw?.salary ? `$${raw.salary}` : "NA",
        Status: raw?.status || "NA",
        Posted: raw?.date_created?.split("T")[0] || "NA",
      };

      const keyRespHtml = raw?.key_responsibilities;
      const keyRespPlain =
        keyRespHtml &&
        String(keyRespHtml).replace(/<[^>]*>/g, "").trim().length > 0
          ? convert(keyRespHtml, { wordwrap: 130 })
          : "N/A";

      const otherDetails = {
        description: convert(raw?.description || "NA", { wordwrap: 130 }),
        keyResponsibilities: keyRespPlain,
        skills: raw?.skills?.join ? raw.skills.join(", ") : raw?.skills || "",
        notes: raw?.notes || "",
      };

      setJobData({ basicDetails, otherDetails });
    } catch (err) {
      console.error("Error fetching job info", err);
    }
  };

  const getQuestionsData = async () => {
    try {
      const response = await axios.post(
        `/question-list-job/${id}`,
        { applyFilter: 0 },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );
      if (response?.data?.message) {
        setQuestionsData(response.data.message);
      }
    } catch (error) {}
  };

  // ── Loading: teeno shimmer dikhao ──
  if (loading) {
    return (
      <Box sx={{ display: "flex", gap: "16px", height: "100%" }}>
        <BasicDetailsShimmer />
        <OtherDetailsShimmer />
        <InternalNotesShimmer />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: "16px", height: "100%" }}>
      {/* ── Basic Details ── */}
      <Card elevation={0} sx={cardSx}>
        <Box sx={cardHeaderSx}>Basic Details</Box>
        <CardContent
          sx={{ padding: "16px 20px", overflowY: "auto", flexGrow: 1 }}
        >
          {Object.keys(jobData).length > 0
            ? Object.entries(jobData?.basicDetails || {}).map(
                ([key, value]) => (
                  <Box
                    key={key}
                    sx={{ marginBottom: "8px" }}
                    display={{ xs: "block", md: "block", lg: "flex" }}
                  >
                    <Typography
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: "400",
                        fontSize: "14px",
                        color: "rgba(0, 0, 0, 0.60)",
                        width: "140px",
                        marginRight: "20px",
                        flexShrink: "0",
                      }}
                    >
                      {key}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "400",
                        fontSize: "14px",
                        color: "rgba(0, 0, 0, 0.87)",
                      }}
                    >
                      {value || "NA"}
                    </Typography>
                  </Box>
                ),
              )
            : "No Data Available"}
        </CardContent>
      </Card>

      {/* ── Other Details ── */}
      <Card elevation={0} sx={cardSx}>
        <Box sx={cardHeaderSx}>Other Details</Box>
        <CardContent
          sx={{ padding: "16px 20px", overflowY: "auto", flexGrow: 1 }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "6px",
              color: "rgba(0, 0, 0, 0.87)",
            }}
          >
            Description
          </Typography>
          <Typography
            ref={descriptionRef}
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "rgba(0, 0, 0, 0.87)",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: showMore ? "unset" : 4,
              overflow: "hidden",
            }}
          >
            {jobData?.otherDetails?.description || "N/A"}
          </Typography>
          {(isOverflowing || showMore) && (
            <Typography
              onClick={toggleShowMore}
              sx={{
                cursor: "pointer",
                color: "#3f95ea",
                fontSize: "14px",
                fontWeight: "500",
                marginTop: "8px",
                marginBottom: "0px",
              }}
            >
              {showMore ? "Read less" : "Read more"}
            </Typography>
          )}

          <Divider sx={{ margin: "12px 0" }} />

          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "6px",
              color: "rgba(0, 0, 0, 0.87)",
            }}
          >
            Key Responsibilities
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "rgba(0, 0, 0, 0.87)",
              whiteSpace: "pre-wrap",
            }}
          >
            {jobData?.otherDetails?.keyResponsibilities || "N/A"}
          </Typography>

          <Divider sx={{ margin: "12px 0" }} />

          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "600",
              marginBottom: "6px",
              color: "rgba(0, 0, 0, 0.87)",
            }}
          >
            Skills
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {jobData?.otherDetails?.skills ? (
              jobData.otherDetails.skills.split(",").map((skill, index) => (
                <Box
                  key={index}
                  sx={{
                    fontSize: "12px",
                    padding: "4px 10px",
                    backgroundColor: "#E3F2FD",
                    borderRadius: "16px",
                    color: "#333",
                  }}
                >
                  {skill.trim()}
                </Box>
              ))
            ) : (
              <Typography sx={{ fontSize: "14px", color: "rgba(0,0,0,0.60)" }}>
                NA
              </Typography>
            )}
          </Box>

          {questionsData.length > 0 && (
            <Box>
              <Divider sx={{ margin: "12px 0" }} />
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "6px",
                  color: "rgba(0, 0, 0, 0.87)",
                }}
              >
                Screening Questions
              </Typography>
              <List sx={{ display: "flex", flexDirection: "column" }}>
                {questionsData.map((question, index) => (
                  <ListItem
                    key={question.question_id || index}
                    disableGutters
                    sx={{ display: "block", pl: 0 }}
                  >
                    <Typography variant="body2">
                      {index + 1}. {questionsType[question.question_type - 1]}{" "}
                      question.
                    </Typography>
                    <Typography sx={{ fontWeight: 600, mt: 0.5, pl: 2 }}>
                      {question.question}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Internal Notes ── */}
      <Card elevation={0} sx={cardSx}>
        <Box sx={cardHeaderSx}>Internal Notes</Box>
        <CardContent
          sx={{
            padding: "16px 20px",
            overflowY: "auto",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: jobData?.otherDetails?.notes ? "flex-start" : "center",
            justifyContent: jobData?.otherDetails?.notes
              ? "flex-start"
              : "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: jobData?.otherDetails?.notes ? 400 : 700,
              color: "text.secondary",
              textAlign: jobData?.otherDetails?.notes ? "left" : "center",
            }}
          >
            {jobData?.otherDetails?.notes || "No notes found"}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default JobInfo;
